import { AssessmentStepKeys, AssessmentType, Categories } from '@/enums'
import {
  extractFunnelFromPathname,
  getLocalStorageItem,
  pushEventToDataLayer,
  useModal,
} from '@/utils'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AssessmentStepper } from '../../AssessmentStepper'
import { SuccessPage } from '../SuccessPage'

interface AssessmentMessages {
  subtitle: string
  titles: Record<string, string>
  subtitles: Record<string, string>
  assessmentSuccessButton: string
  assessmentSuccessSubtitle: string
  assessmentSuccessTitle: string
}

const useMessages = (): AssessmentMessages => {
  const { t } = useTranslation()

  return {
    subtitle: t('leadStepSubtitle'),
    titles: {
      [AssessmentStepKeys.businessType]: t('businessTypeTitle'),
      [AssessmentStepKeys.companyFoundedDateLead]: t('companyFoundedDateTitle'),
      [AssessmentStepKeys.contactData]: t('createLeadTitle'),
      [AssessmentStepKeys.approxLaborCosts]: t('approxLaborCostsTitle'),
      [AssessmentStepKeys.approxRevenue]: t('approxRevenueTitle'),
      [AssessmentStepKeys.employeesCount]: t('employeesCountTitle'),
      [AssessmentStepKeys.createLead]: t('createLeadTitle'),
      [AssessmentStepKeys.inventory]: t('inventoryTitle'),
      [AssessmentStepKeys.constructionArea]: t('constructionAreaTitle'),
      [AssessmentStepKeys.constructionDetails]: t('constructionDetailsTitle'),
      [AssessmentStepKeys.constructionDate]: t('constructionDateTitle'),
    },
    subtitles: {
      [AssessmentStepKeys.companyFoundedDateLead]: t(
        'companyFoundedDateSubtitle',
      ),
      [AssessmentStepKeys.contactData]: t('contactDataLeadSubtitle'),
      [AssessmentStepKeys.approxLaborCosts]: t('approxLaborCostsSubtitle'),
      [AssessmentStepKeys.approxRevenue]: t('approxRevenueSubtitle'),
      [AssessmentStepKeys.employeesCount]: t('employeesCountSubtitle'),
    },
    assessmentSuccessButton: t('assessmentSuccessButton'),
    assessmentSuccessSubtitle: t('assessmentSuccessSubtitle'),
    assessmentSuccessTitle: t('assessmentSuccessTitle'),
  }
}

interface LeadGenerationAssessmentProps {
  onLogoClick?: () => void
  categoryId: keyof typeof Categories
  stepKeys: string[]
  stepProps: Record<string, any>
  stepperBreadcrumbTitles?: Record<string, any>
}

export const LeadGenerationAssessment = ({
  onLogoClick,
  categoryId,
  stepKeys,
  stepProps,
  stepperBreadcrumbTitles,
}: LeadGenerationAssessmentProps) => {
  const messages = useMessages()

  const finishedState = useModal()

  const stepLocalStorageKey = `lead-${categoryId}-current-step`
  const valuesLocalStorageKey = `lead-${categoryId}-values`

  const currentStep = useMemo(
    () => getLocalStorageItem(stepLocalStorageKey),
    [],
  )

  const onFinish = () => {
    finishedState.open()

    pushEventToDataLayer('Step Change', {
      funnel_product: extractFunnelFromPathname(),
      funnel_step: 'funnel_finished',
    })
  }

  return finishedState.isOpen ? (
    <SuccessPage onLogoClick={onLogoClick} />
  ) : (
    <AssessmentStepper
      initialStep={currentStep}
      initialValues={getLocalStorageItem(valuesLocalStorageKey)}
      messages={messages}
      onFinish={onFinish}
      onLogoClick={onLogoClick}
      skipSuccessPage
      stepKeys={stepKeys}
      stepLocalStorageKey={stepLocalStorageKey}
      stepperBreadcrumbTitles={stepperBreadcrumbTitles}
      stepProps={stepProps}
      type={AssessmentType.firstTimeUser}
      valuesLocalStorageKey={valuesLocalStorageKey}
    />
  )
}
